import type { CrmDisplayConfigField, CrmDisplayConfigTab } from '@plato/data/model/crm-config';
import { LoadingSpinner } from '@plato/ui-legacy/components/loading/loading-spinner';
import { cn } from '@plato/ui/util/classes';
import { useId } from 'react';
import { useCrmFieldConfig } from './crm-context.tsx';
import type { CrmFieldMode } from './crm-field.tsx';
import { CrmFieldValue, getFieldKey } from './crm-field.tsx';
import { CrmFieldLabel } from './crm-label.tsx';

export type CrmTabProps = {
    config: CrmDisplayConfigTab;
    data?: object;
    disabled?: boolean;
    mode?: CrmFieldMode;
    onAbandon?: () => void;
    onSave?: () => void;
};

export function CrmTab({ config, data, disabled, mode, onAbandon, onSave }: CrmTabProps) {
    return (
        <form
            className="w-full"
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    // a textarea owns plain Enter; Cmd/Ctrl+Enter still submits the tab
                    if (e.target instanceof HTMLTextAreaElement && !e.metaKey && !e.ctrlKey) {
                        return;
                    }
                    if (mode === 'edit') {
                        onSave?.();
                    }
                } else if (e.key === 'Escape') {
                    onAbandon?.();
                    e.preventDefault();
                    e.stopPropagation();
                }
            }}
        >
            <fieldset disabled={disabled} className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 w-full p-0">
                {data ? (
                    config.fields.map((field) => (
                        <CrmField key={getFieldKey(field)} config={field} mode={mode} />
                    ))
                ) : (
                    <LoadingSpinner />
                )}
            </fieldset>
        </form>
    );
}

export function getTabKey(tab: CrmDisplayConfigTab) {
    // simplest way to get a unique value for the tab config
    return JSON.stringify(tab);
}

type CrmFieldProps = {
    config: CrmDisplayConfigField;
    mode?: CrmFieldMode;
};

export function CrmField(props: CrmFieldProps) {
    const config = useCrmFieldConfig(props.config);
    const id = useId();
    const isMultiline = config.format === 'multiline';

    return (
        <div className={cn('py-1 border-b border-neutral-100', isMultiline && 'sm:col-span-1')}>
            <div className={cn('grid grid-cols-5', isMultiline ? 'items-start' : 'items-center')}>
                <CrmFieldLabel
                    htmlFor={id}
                    className={cn('px-3 col-span-1', isMultiline && 'pt-4')}
                    variant="thin"
                >
                    {config.label}
                </CrmFieldLabel>
                {/* min-w-0: a wide value must shrink inside its track, not push the copy button out of line */}
                <div className="col-span-3 min-w-0">
                    <CrmFieldValue config={config} id={id} showCopy="always" mode={props.mode} />
                </div>
            </div>
        </div>
    );
}
